///
/// EarthBrowser
///
/// Created by Matt Giger on 9/17/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Placemark creation helpers: "Save this place" on a focused entity
/// (coordinates extracted from the info window content) and direct
/// creation at coordinates. Follows saved views' capture-not-type
/// philosophy: create instantly with a sensible name, rename after.
///

import {
	cDefaultPlacemarkColor,
	upsertPlacemark,
	type Placemark,
	type PlacemarkLocation,
} from "@/redux/placemarksSlice"
import { appStore } from "@/redux/store"
import type { InfoWindowContentData } from "@/redux/userInterfaceSlice"

/**
 * Create a single-location placemark and store it. Returns the new
 * placemark so callers can open a rename prompt or fly to it.
 */
export function addPlacemark(lat: number, lon: number, name: string, altitudeM?: number): Placemark {
	const now = new Date().toISOString()
	const location: PlacemarkLocation = {
		id: crypto.randomUUID(),
		lat,
		lon,
		altitudeM,
	}
	const placemark: Placemark = {
		id: crypto.randomUUID(),
		name: name.trim().length > 0 ? name.trim() : formatCoordsName(lat, lon),
		locations: [location],
		color: cDefaultPlacemarkColor,
		tags: [],
		createdAt: now,
		updatedAt: now,
	}
	appStore.dispatch(upsertPlacemark(placemark))
	return placemark
}

/** "37.77°N, 122.42°W" fallback name for unnamed spots */
export function formatCoordsName(lat: number, lon: number): string {
	const latLabel = `${Math.abs(lat).toFixed(2)}°${lat >= 0 ? "N" : "S"}`
	const lonLabel = `${Math.abs(lon).toFixed(2)}°${lon >= 0 ? "E" : "W"}`
	return `${latLabel}, ${lonLabel}`
}

/**
 * Coordinates of the currently focused entity, extracted from the info
 * window content. Returns null for content without a usable ground
 * location (radar stations carry no coords; a focused placemark is
 * already saved).
 */
export function focusedEntityCoords(content: InfoWindowContentData | null): { lat: number; lon: number } | null {
	switch (content?.type) {
		case "location":
			return { lat: content.locationInfo.lat, lon: content.locationInfo.lon }
		case "webcam":
			return { lat: content.webcam.lat, lon: content.webcam.lon }
		case "buoy":
			return { lat: content.buoy.lat, lon: content.buoy.lon }
		case "earthquake":
			return { lat: content.earthquake.lat, lon: content.earthquake.lon }
		case "volcano":
			return { lat: content.volcano.latitude, lon: content.volcano.longitude }
		case "wildfire":
			return { lat: content.wildfire.centroid_lat, lon: content.wildfire.centroid_lon }
		case "satellite":
			return content.livePosition
				? { lat: content.livePosition.lat, lon: content.livePosition.lon }
				: null
		default:
			return null
	}
}

/**
 * Save the currently focused entity as a placemark, named after the info
 * window title ("Kīlauea", "Pier 39 Cam"). Returns the created placemark,
 * or null when nothing focusable-as-a-place is focused.
 */
export function savePlacemarkAtFocus(): Placemark | null {
	const infoWindow = appStore.getState().userInterface.infoWindow
	if (!infoWindow.isOpen) return null

	const coords = focusedEntityCoords(infoWindow.contentData)
	if (!coords) return null

	return addPlacemark(coords.lat, coords.lon, infoWindow.title)
}
